/**
 * Shared pill button HTML for email templates.
 * Used by NewsletterEditor templates and Quill blots.
 * @param {object} [opts]
 * @param {boolean} [opts.stretchInCell] — fill parent &lt;td&gt; (use inside a fixed-width row so 3 pills sit inline in ~600px)
 * @param {boolean} [opts.hiDef] — with stretchInCell: Lead row — fixed pill height, width grows with label (single line), stronger border/shadow
 */
export function emailPillButtonHtml(text, url, variant = 'primary', opts = {}) {
    const stretch = !!opts.stretchInCell;
    const hiDef = !!opts.hiDef && stretch;
    const isPrimary = variant === 'primary';
    const bg = isPrimary ? '#B99040' : '#8B6F2E';
    const stroke = isPrimary ? '#7a6230' : '#6b5330';
    const defaultW = isPrimary ? '220' : '200';
    const w = stretch ? null : (opts.widthPx != null ? String(opts.widthPx) : defaultW);
    const h = stretch ? (hiDef ? '48' : '44') : '48';
    const fontPt = stretch ? (hiDef ? '13pt' : '11pt') : '15pt';
    const fontPx = stretch ? (hiDef ? '15' : '12') : '15';
    const borderColor = isPrimary ? '#7a5f28' : '#5c4820';
    const borderW = hiDef ? '2px' : '1px';
    const shadow = hiDef ? '0 3px 8px rgba(0,0,0,0.18)' : '0 2px 4px rgba(0,0,0,0.12)';

    /** Lead row: same height for every pill; width follows text (single line). */
    const fixedHeightAutoWidth = stretch && hiDef;
    const pillH = '52';
    const label = String(text || '');
    const msoW = fixedHeightAutoWidth
        ? String(Math.min(340, Math.max(120, Math.round(label.length * 7.4) + 52)))
        : stretch
          ? hiDef
              ? '180'
              : '188'
          : w;

    let outerTableStyle;
    let aStyle;
    let widthAttr;

    if (fixedHeightAutoWidth) {
        outerTableStyle =
            'width:auto;max-width:100%;margin:2px auto;border-collapse:separate;mso-table-lspace:0pt;mso-table-rspace:0pt;display:table;vertical-align:middle;';
        widthAttr = '';
        aStyle = `display:inline-block;width:auto;max-width:100%;box-sizing:border-box;background-color:${bg};color:#ffffff !important;text-decoration:none !important;height:${pillH}px;line-height:${pillH}px;white-space:nowrap;padding:0 14px;font-size:${fontPx}px;font-weight:700;font-family:Arial,Helvetica,sans-serif;border-radius:24px;text-align:center;border:${borderW} solid ${borderColor};box-shadow:${shadow};letter-spacing:0.4px;-webkit-font-smoothing:antialiased;`;
    } else if (stretch) {
        outerTableStyle = `width:100%;max-width:100%;margin:${hiDef ? '2px' : '4px'} 0;border-collapse:separate;mso-table-lspace:0pt;mso-table-rspace:0pt;display:table;vertical-align:middle;`;
        widthAttr = ' width="100%"';
        aStyle = `display:block;width:100%;max-width:100%;box-sizing:border-box;background-color:${bg};color:#ffffff !important;text-decoration:none !important;min-height:${h}px;line-height:1.3;padding:${hiDef ? '12px 8px' : '10px 6px'};font-size:${fontPx}px;font-weight:700;font-family:Arial,Helvetica,sans-serif;border-radius:24px;text-align:center;border:${borderW} solid ${borderColor};box-shadow:${shadow};letter-spacing:${hiDef ? '0.4px' : '0.3px'};word-wrap:break-word;-webkit-font-smoothing:antialiased;`;
    } else {
        outerTableStyle =
            'margin:10px 8px;border-collapse:separate;mso-table-lspace:0pt;mso-table-rspace:0pt;display:inline-table;vertical-align:middle;';
        widthAttr = '';
        aStyle = `display:inline-block;background-color:${bg};color:#ffffff !important;text-decoration:none !important;width:${w}px;height:${h}px;line-height:${h}px;font-size:${fontPx}px;font-weight:bold;font-family:Arial,Helvetica,sans-serif;border-radius:24px;text-align:center;border:1px solid ${borderColor};box-shadow:0 2px 4px rgba(0,0,0,0.12);letter-spacing:0.5px;`;
    }

    /** Inner <td> must not repeat border/shadow/radius — that stacks with <a> / VML and reads as blurry on many clients. */
    const tdWrap = 'padding:0;margin:0;border:0;vertical-align:middle;';

    return `
<table class="email-pill-btn" align="center" cellpadding="0" cellspacing="0" border="0"${widthAttr} style="${outerTableStyle}">
<tr><td align="center" style="${tdWrap}">
<!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:office" href="${url}" style="height:${fixedHeightAutoWidth ? pillH : h}px;v-text-anchor:middle;width:${msoW}px;" arcsize="50%" strokecolor="${stroke}" strokeweight="${hiDef ? '2px' : '1px'}" fillcolor="${bg}"><v:stroke/><w:anchorlock/><center style="color:#ffffff;font-family:Arial,sans-serif;font-size:${fontPt};font-weight:bold;letter-spacing:${hiDef ? '0.4px' : '0.5px'};-webkit-font-smoothing:antialiased;">${text}</center></v:roundrect><![endif]-->
<!--[if !mso]><!-->
<a href="${url}" target="_blank" style="${aStyle}">
${text}
</a>
<!--<![endif]-->
</td></tr></table>`;
}
